import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { database, supabase } from '../supabase';
import './Account.css';

type AccountPage = 'dashboard' | 'orders' | 'settings' | 'addresses';

interface Order {
  id: string;
  created_at: string;
  total_amount: number;
  status: string;
  payment_status: string;
  order_items?: any[];
}

const AccountSettings: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const { user } = useAuth() as any;
  const [userData, setUserData] = useState({ firstName: '', lastName: '', email: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProfile = async () => {
      if (user) {
        const { data: profile } = await database.getUserProfile(user.id);
        if (profile) {
          setUserData({ firstName: profile.first_name || '', lastName: profile.last_name || '', email: user.email || '' });
        }
      }
      setLoading(false);
    };
    loadProfile();
  }, [user]);

  return (
    <div className="account-page">
      <div className="page-header">
        <button className="back-btn" onClick={onBack}>← Back</button>
        <h1>Account Settings</h1>
      </div>
      <div className="page-content">
        {loading ? <p>Loading...</p> : (
          <form className="settings-form">
            <div className="form-section">
              <h3>Personal Information</h3>
              <div className="form-group">
                <label>First Name</label>
                <input type="text" value={userData.firstName} onChange={(e) => setUserData(prev => ({ ...prev, firstName: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input type="text" value={userData.lastName} onChange={(e) => setUserData(prev => ({ ...prev, lastName: e.target.value }))} />
              </div>
            </div>
            <button type="submit" className="save-btn">Save Changes</button>
          </form>
        )}
      </div>
    </div>
  );
};

const OrderHistory: React.FC<{ onBack: () => void; userId: string }> = ({ onBack, userId }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  const { user: authUser } = useAuth() as any;

  useEffect(() => {
    const loadOrders = async () => {
      try {
        const email = authUser?.email;

        console.log('Attempting to load orders for user_id:', userId);
        if (userId) {
          const { data, error } = await database.getUserOrders(userId);
          if (error) throw error;
          if (data && data.length > 0) {
            console.log('Orders found by user_id:', data.length);
            setOrders(data as any[]);
            setLoading(false);
            return;
          }
          console.log('No orders by user_id, falling back to email if available');
        }

        if (email) {
          console.log('Querying orders by customer_email (case-insensitive):', email);
          const { data: byEmail, error: emailErr } = await supabase
            .from('orders')
            .select('*, order_items(*, products(name,image))')
            .ilike('customer_email', email)
            .order('created_at', { ascending: false });
          if (emailErr) throw emailErr;
          console.log('Orders found by email:', byEmail?.length || 0);
          if (byEmail && byEmail.length > 0) {
            setOrders(byEmail as any[]);
            setLoading(false);
            return;
          }
        }

        // nothing found
        setOrders([]);
      } catch (error) {
        console.error('Error loading orders:', error);
      }
      setLoading(false);
    };
    loadOrders();
  }, [userId, authUser?.email]);

  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });

  if (loading) {
    return (
      <div className="account-page">
        <div className="page-header">
          <button className="back-btn" onClick={onBack}>← Back</button>
          <h1>Order History</h1>
        </div>
        <div className="page-content" style={{ textAlign: 'center', padding: '40px' }}>Loading orders...</div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="account-page">
        <div className="page-header">
          <button className="back-btn" onClick={onBack}>← Back</button>
          <h1>Order History</h1>
        </div>
        <div className="page-content">
          <div className="no-orders">
            <h2>No orders yet</h2>
            <p>You haven't placed any orders yet.</p>
            <button className="shop-btn" onClick={() => window.location.hash = '#shop'}>Start Shopping</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="account-page">
      <div className="page-header">
        <button className="back-btn" onClick={onBack}>← Back</button>
        <h1>Order History</h1>
      </div>
      <div className="page-content">
        <div className="orders-list">
          {orders.map((order) => (
            <div key={order.id} className="order-card">
              <div className="order-header" onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}>
                <div className="order-info">
                  <span className="order-id">#{order.id.slice(0, 8).toUpperCase()}</span>
                  <span className="order-date">{formatDate(order.created_at)}</span>
                </div>
                <div className="order-meta">
                  <span className="order-total">€{order.total_amount.toFixed(2)}</span>
                  <span className={`status-badge ${order.payment_status === 'completed' ? 'completed' : 'pending'}`}>
                    {order.payment_status === 'completed' ? 'Paid' : 'Pending'}
                  </span>
                  <span className="expand-icon">▼</span>
                </div>
              </div>
              {expandedOrder === order.id && (
                <div className="order-details">
                  <div className="order-items">
                      {order.order_items?.map((item: any) => (
                        <div key={item.id} className="order-item">
                          <img src={item.products?.images?.[0] || item.products?.image || item.image || ''} alt={item.products?.name || 'Product'} className="order-item-image" />
                          <div className="order-item-info">
                            <span className="item-name">{item.products?.name || item.product_id}</span>
                            <span className="item-qty">Qty: {item.quantity}</span>
                          </div>
                          <div className="order-item-pricing">
                            <span className="item-price">€{(item.price || 0).toFixed(2)}</span>
                            <span className="item-subtotal">€{((item.price || 0) * (item.quantity || 1)).toFixed(2)}</span>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Account: React.FC = () => {
  const { isAuthenticated, user, loading } = useAuth() as any;
  const [currentPage, setCurrentPage] = useState<AccountPage>('dashboard');

  if (loading) {
    return (
      <div className="account">
        <div className="account-container" style={{ textAlign: 'center', padding: '80px 20px' }}>
          <h1>Loading...</h1>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return (
      <div className="account">
        <div className="account-container" style={{ textAlign: 'center', padding: '80px 20px' }}>
          <h1>Please Sign In</h1>
          <p>You need to be signed in to access your account.</p>
          <button className="account-btn" onClick={() => window.location.hash = '#signin'} style={{ marginTop: '20px' }}>Go to Sign In</button>
        </div>
      </div>
    );
  }

  return (
    <div className="account">
      <div className="account-container">
        {currentPage === 'dashboard' && (
          <div className="account-dashboard">
            <h1>My Account</h1>
            <p>Welcome to your ANTI-STYLE HYPOTHESIS account</p>
            <div className="account-sections">
              <div className="account-section" onClick={() => setCurrentPage('orders')}>
                <h2>Order History</h2>
                <p>View and track your orders.</p>
                <button className="account-btn">View Orders</button>
              </div>
              <div className="account-section" onClick={() => setCurrentPage('settings')}>
                <h2>Account Settings</h2>
                <p>Manage your account information.</p>
                <button className="account-btn">Edit Profile</button>
              </div>
            </div>
          </div>
        )}
        {currentPage === 'orders' && <OrderHistory onBack={() => setCurrentPage('dashboard')} userId={user.id} />}
        {currentPage === 'settings' && <AccountSettings onBack={() => setCurrentPage('dashboard')} />}
      </div>
    </div>
  );
};

export default Account;
